import { factory } from '../../../../factory-api/factory'
import { defineStore } from 'pinia'
import type { IFileUploadRequest } from '@/interfaces/IFileUploadRequest'
import { Directory, Filesystem, type WriteFileResult } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'
import { FileOpener, type FileOpenerOptions } from '@capacitor-community/file-opener'
import type { SelectItem } from '@/interfaces/components/ISelectComponent'

export const useFileStore = defineStore('file', () => {
  const selectedFiles = ref<IFileUploadRequest[]>([])
  const uploadingInProgress = ref<boolean>(false)
  const defaultSelectItem = ref<SelectItem>({
    name: '',
    id: ''
  })

  const getUserImage = async (userId: string) => {
    try {
      if (userId == undefined || userId === '') {
        return ''
      }
      const fileApi = await factory.getFileApi()
      const response = await fileApi.userImageBase64({ userId: userId })

      return response
    } catch (error) {
      console.log(error)
      return
    }
  }

  const setSelectedFile = (file: IFileUploadRequest) => {
    selectedFiles.value.push(file)
  }

  const updateSelectedFile = (
    file: IFileUploadRequest,
    isUploaded: boolean,
    selectItem?: SelectItem
  ) => {
    const index = selectedFiles.value.indexOf(file)

    if (index !== -1) {
      selectedFiles.value[index].isUploaded = isUploaded
      if (selectItem !== undefined) {
        selectedFiles.value[index].selectItem = selectItem
      }
    }
  }

  const resetFileStore = () => {
    selectedFiles.value = []
    uploadingInProgress.value = false
  }

  const removeSelectedFile = (file: IFileUploadRequest) => {
    if (file !== undefined) {
      const index = selectedFiles.value.indexOf(file)

      if (index > -1) {
        selectedFiles.value.splice(index, 1)
      }
    }
  }

  const setUploadingInProgress = (inProgress: boolean) => {
    uploadingInProgress.value = inProgress
  }

  const setSelectedFiles = (files: IFileUploadRequest[]) => {
    selectedFiles.value = files
  }

  const downloadFile = async (blob: Blob, fileName: string) => {
    try {
      if (Capacitor.isNativePlatform() === true) {
        // Native download
        // First see if we have permission to write the file
        let permission = await Filesystem.checkPermissions()
        if (permission.publicStorage !== 'granted') {
          // If not granted ask for permission
          permission = await Filesystem.requestPermissions()

          if (permission.publicStorage !== 'granted') {
            // If still no permission we abort the download
            console.warn('Download was aborted due to mission permission to download')
            return
          }
        } else {
          const reader = new FileReader()
          reader.readAsDataURL(blob)

          reader.onloadend = async () => {
            const writeFileResult: WriteFileResult = await Filesystem.writeFile({
              path: 'AS3/' + fileName,
              data: reader.result as string,
              directory: Directory.Documents,
              recursive: true
            })
            await openFile(writeFileResult.uri)
          }
        }
      } else {
        // Web download
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        link.click()

        // Clean up after downloading
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Error downloading the file: ', error)
    }
  }

  const openFile = async (path: string) => {
    try {
      const fileOpenerOptions: FileOpenerOptions = {
        filePath: path,
        openWithDefault: true
      }
      await FileOpener.open(fileOpenerOptions)
    } catch (e) {
      console.log('Error opening file', e)
    }
  }

  const setDefaultSelectItem = (defaultText: string) => {
    defaultSelectItem.value = {
      name: defaultText
    }
  }

  return {
    getUserImage,
    selectedFiles,
    setSelectedFiles,
    setSelectedFile,
    resetFileStore,
    removeSelectedFile,
    updateSelectedFile,
    setUploadingInProgress,
    uploadingInProgress,
    downloadFile,
    openFile,
    defaultSelectItem,
    setDefaultSelectItem
  }
})
